import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Minimal hook for URL-based filters. Reads/writes search params with defaults.
 */
export function useUrlFilters(defaults = {}) {
    const [searchParams, setSearchParams] = useSearchParams();

    // Build filters object from URL params, falling back to defaults
    const filters = {};
    for (const [key, defaultValue] of Object.entries(defaults)) {
        const urlValue = searchParams.get(key);
        if (urlValue !== null) {
            // Parse numbers if default is a number
            filters[key] = typeof defaultValue === 'number' ? Number(urlValue) : urlValue;
        } else {
            filters[key] = defaultValue;
        }
    }

    // Update a single filter
    const setFilter = useCallback((key, value) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set(key, String(value));
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // Update multiple filters at once
    const setFilters = useCallback((updates) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            for (const [key, value] of Object.entries(updates)) {
                next.set(key, String(value));
            }
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // Sync defaults to URL on mount (only missing keys)
    useEffect(() => {
        const missing = Object.entries(defaults).filter(([key]) => !searchParams.has(key));
        if (missing.length > 0) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                for (const [key, value] of missing) {
                    next.set(key, String(value));
                }
                return next;
            }, { replace: true });
        }
    }, []);

    return { filters, setFilter, setFilters };
}

export default useUrlFilters;
